// src/pages/motolivraison/commerces.page.jsx
//
// Liste de tous les commerces du module MotoLivraison avec filtres et actions admin :
// validation, suspension, réjection, réactivation.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, message, Button, Card, Row, Col, Tag, Tooltip, Space,
  Breadcrumb, Statistic, Avatar, Select, Form, Dropdown
} from 'antd';
import {
  CheckCircleOutlined, StopOutlined, EyeOutlined, PlayCircleOutlined,
  ShopOutlined, RightOutlined, SearchOutlined, DownOutlined, CloseCircleOutlined,
  ClockCircleOutlined, UserAddOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';

const Commerces = () => {
  const [commerces, setCommerces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', q: '' });
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedCommerce, setSelectedCommerce] = useState(null);
  const [rejectForm] = Form.useForm();
  const [createCommercantModalOpen, setCreateCommercantModalOpen] = useState(false);
  const [createCommercantForm] = Form.useForm();
  const [creatingCommercant, setCreatingCommercant] = useState(false);
  const navigate = useNavigate();

  const stats = {
    total: commerces.length,
    pending: commerces.filter(c => c.status === 'pending').length,
    approved: commerces.filter(c => c.status === 'approved').length,
    suspended: commerces.filter(c => c.status === 'suspended').length,
  };

  const fetchCommerces = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listCommerces({
        status: filters.status || undefined,
        q: filters.q || undefined,
      });
      setCommerces(data?.commerces || []);
    } catch (e) {
      message.error('Erreur lors du chargement des commerces');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCommerces();
  }, [fetchCommerces]);

  const handleAction = async (action, commerce) => {
    try {
      const labels = {
        validate: 'approuvé',
        suspend: 'suspendu',
        reactivate: 'réactivé',
      };
      if (action === 'validate') await motolivraisonService.validateCommerce(commerce._id);
      else if (action === 'suspend') await motolivraisonService.suspendCommerce(commerce._id);
      else if (action === 'reactivate') await motolivraisonService.reactivateCommerce(commerce._id);
      message.success(`Commerce ${labels[action]}`);
      fetchCommerces();
    } catch (e) {
      message.error(e?.message || 'Erreur lors de l\'action');
    }
  };

  const openReject = (commerce) => {
    setSelectedCommerce(commerce);
    rejectForm.resetFields();
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    try {
      const { reason } = await rejectForm.validateFields();
      await motolivraisonService.rejectCommerce(selectedCommerce._id, reason);
      message.success('Commerce rejeté');
      setRejectModalOpen(false);
      fetchCommerces();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors du rejet');
    }
  };

  const openCreateCommercant = () => {
    createCommercantForm.resetFields();
    setCreateCommercantModalOpen(true);
  };

  const handleCreateCommercant = async () => {
    try {
      const values = await createCommercantForm.validateFields();
      setCreatingCommercant(true);
      const res = await motolivraisonService.createCommercantByAdmin(values);
      const successMsg = res?.userCreated
        ? `Commerçant créé. Mot de passe par défaut : 123456`
        : (res?.profileAlreadyExists
            ? 'Cet utilisateur est déjà commerçant approuvé'
            : 'Profil commerçant ajouté à un utilisateur existant');
      message.success(successMsg);
      setCreateCommercantModalOpen(false);
      fetchCommerces();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors de la création');
    } finally {
      setCreatingCommercant(false);
    }
  };

  const statusColor = (s) => ({
    pending: 'orange',
    approved: 'green',
    suspended: 'red',
    rejected: 'default',
  })[s] || 'default';

  const statusLabel = (s) => ({
    pending: 'En attente',
    approved: 'Approuvé',
    suspended: 'Suspendu',
    rejected: 'Rejeté',
  })[s] || s;

  const columns = [
    {
      title: 'Commerce',
      key: 'cat',
      render: (_, r) => (
        <Space>
          <Avatar
            size={48}
            src={r.logo}
            icon={<ShopOutlined />}
            style={{ backgroundColor: '#fa8c16' }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{r.nom}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {r.categorieId?.nom || '—'}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Propriétaire',
      dataIndex: 'ownerId',
      render: (o) => o ? (
        <div>
          <div style={{ fontSize: 13 }}>{o.firstName} {o.lastName}</div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>{o.phone}</div>
        </div>
      ) : '—',
    },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      render: (v, r) => (
        <div style={{ fontSize: 12 }}>
          <div>{v}</div>
          {r.quartier && <div style={{ color: '#8c8c8c' }}>{r.quartier}</div>}
        </div>
      ),
    },
    {
      title: 'Ville',
      dataIndex: 'cityId',
      width: 100,
      render: (c) => c?.name || '—',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      width: 120,
      render: (s) => <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, r) => {
        const items = [];
        if (r.status === 'pending') {
          items.push({
            key: 'validate',
            icon: <CheckCircleOutlined style={{ color: 'green' }} />,
            label: 'Approuver',
            onClick: () => handleAction('validate', r),
          });
          items.push({
            key: 'reject',
            icon: <CloseCircleOutlined style={{ color: 'red' }} />,
            label: 'Rejeter',
            onClick: () => openReject(r),
          });
        }
        if (r.status === 'approved') {
          items.push({
            key: 'suspend',
            icon: <StopOutlined style={{ color: 'red' }} />,
            label: 'Suspendre',
            onClick: () => handleAction('suspend', r),
          });
        }
        if (r.status === 'suspended') {
          items.push({
            key: 'reactivate',
            icon: <PlayCircleOutlined style={{ color: 'green' }} />,
            label: 'Réactiver',
            onClick: () => handleAction('reactivate', r),
          });
        }
        return (
          <Space>
            <Tooltip title="Voir détail">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => navigate(`/dashboard/motolivraison/commerces/${r._id}`)}
              />
            </Tooltip>
            {items.length > 0 && (
              <Dropdown menu={{ items }}>
                <Button size="small">
                  Actions <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined style={{ fontSize: 12 }} />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Commerces</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={6}><Card><Statistic title="Total" value={stats.total} prefix={<ShopOutlined />} /></Card></Col>
          <Col xs={6}><Card><Statistic title="En attente" value={stats.pending} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="Approuvés" value={stats.approved} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="Suspendus" value={stats.suspended} prefix={<StopOutlined />} valueStyle={{ color: '#f5222d' }} /></Card></Col>
        </Row>

        <Card
          title="Commerces MotoLivraison"
          extra={
            <Button type="primary" icon={<UserAddOutlined />} onClick={openCreateCommercant}>
              Nouveau commerçant
            </Button>
          }
        >
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={10}>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Rechercher un commerce..."
                allowClear
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={6}>
              <Select
                placeholder="Statut"
                allowClear
                style={{ width: '100%' }}
                value={filters.status || undefined}
                onChange={(v) => setFilters({ ...filters, status: v || '' })}
                options={[
                  { value: 'pending', label: 'En attente' },
                  { value: 'approved', label: 'Approuvés' },
                  { value: 'suspended', label: 'Suspendus' },
                  { value: 'rejected', label: 'Rejetés' },
                ]}
              />
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={commerces.map(c => ({ ...c, key: c._id }))}
            loading={loading}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 1000 }}
          />
        </Card>
      </div>

      <Modal
        title="Rejeter le commerce"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleReject}
        okText="Confirmer le rejet"
        cancelText="Annuler"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Raison du rejet"
            rules={[{ required: true, message: 'La raison est requise' }]}
          >
            <Input.TextArea rows={3} placeholder="Expliquez pourquoi ce commerce est rejeté..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Create Commerçant */}
      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8 }} />
            Nouveau commerçant
          </span>
        }
        open={createCommercantModalOpen}
        onCancel={() => setCreateCommercantModalOpen(false)}
        onOk={handleCreateCommercant}
        okText="Créer"
        cancelText="Annuler"
        confirmLoading={creatingCommercant}
        width={560}
      >
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 6,
          }}
        >
          <strong>ℹ️ Mot de passe par défaut :</strong>{' '}
          <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 4 }}>123456</code>
          <br />
          <small style={{ color: '#8c8c8c' }}>
            Le commerçant pourra le changer après sa première connexion. Si le numéro existe déjà,
            le profil commerçant sera simplement ajouté à l'utilisateur existant (sans modifier son mot de passe).
          </small>
        </div>
        <Form form={createCommercantForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Prénom"
                rules={[{ required: true, message: 'Prénom requis' }]}
              >
                <Input placeholder="Ex: Jean" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Nom">
                <Input placeholder="Ex: Kouassi" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="phone"
            label="Téléphone"
            rules={[{ required: true, message: 'Téléphone requis' }]}
            extra="Format : +225XXXXXXXX"
          >
            <Input placeholder="+225..." />
          </Form.Item>
          <Form.Item name="email" label="Email (optionnel)">
            <Input type="email" placeholder="email@exemple.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Commerces;
